import type { FlashData } from '@inertiajs/core';
import type { Emitter, FlashMessagePayload } from '@/types/emitter';

export interface ToastResponse {
    status: number;
    config?: { method?: string };
    data?: { message?: string };
}

export interface ToastError {
    code?: string;
    config?: { responseType?: string };
    response?: { status?: number; data?: { message?: string; error?: string } };
}

interface ToastAxiosInstance {
    interceptors: {
        response: {
            use: (
                onFulfilled: (response: ToastResponse) => ToastResponse,
                onRejected: (error: ToastError) => Promise<never>
            ) => void;
        };
    };
}

export function registerInertiaFlashListener(
    router: { on: (name: string, cb: (e: unknown) => void) => void },
    emitter: Emitter
): void {
    router.on('flash', (e) => {
        const toast = (e as { detail?: { flash?: FlashData } })?.detail?.flash?.toast;
        if (toast) {
            emitter.emit('flash-message', notificationToFlash(toast));
        }
    });

    // Any Inertia visit that returns validation errors (422) toasts the first one.
    // Inline Precognition validation uses a fetch client and does not fire this event.
    // A visit without an errorBag (e.g. the notification poll) can receive another form's
    // named bag unscoped, as { bagName: { field: message } }.
    router.on('error', (e) => {
        const errors = (e as { detail?: { errors?: Record<string, string | Record<string, string>> } })?.detail?.errors;
        let first = errors && Object.values(errors)[0];
        if (first && typeof first === 'object') {
            first = Object.values(first)[0];
        }
        if (typeof first === 'string' && first) {
            emitter.emit('flash-message', { type: 'error', text: first });
        }
    });
}

const SKIP_STATUSES = new Set([419]);

export function setupResponseToastInterceptor(axiosInstance: ToastAxiosInstance, emitter: Emitter): void {
    axiosInstance.interceptors.response.use(
        (response) => {
            const method = (response.config?.method ?? 'get').toLowerCase();
            const message = response.data?.message;
            if (
                method !== 'get' &&
                response.status >= 200 &&
                response.status < 300 &&
                typeof message === 'string' &&
                message.length > 0
            ) {
                emitter.emit('flash-message', { type: 'success', text: message });
            }
            return response;
        },
        (error) => {
            // Canceled requests are intentional; blob responses can't carry a JSON
            // message, so their callers own the error toast.
            if (error?.code === 'ERR_CANCELED' || error?.config?.responseType === 'blob') {
                return Promise.reject(error);
            }
            const status = error?.response?.status;
            if (!status) {
                // No response at all (network failure/timeout) — callers rely on
                // this toast; they no longer emit their own in catch blocks.
                emitter.emit('flash-message', {
                    type: 'error',
                    text: 'Something went wrong. Please check your connection and try again.',
                });
            } else if (!SKIP_STATUSES.has(status)) {
                const data = error.response?.data;
                emitter.emit('flash-message', {
                    type: 'error',
                    text: data?.message ?? data?.error ?? 'Something went wrong.',
                });
            }
            return Promise.reject(error);
        }
    );
}

export function notificationToFlash(n: App.Data.FlashBag): FlashMessagePayload {
    const payload: FlashMessagePayload = { type: n.type, text: n.message };
    if (n.title) {
        payload.title = n.title;
    }
    if (n.link && n.linkText) {
        payload.link = n.link;
        payload.linkText = n.linkText;
    }
    payload.duration = n.duration;
    return payload;
}
